import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, finalize, shareReplay, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface ApiTokenResponse {
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
  refreshExpiresIn?: number;
  role?: { name?: string };
}

@Injectable({ providedIn: 'root' })
export class ApiAuthService {
  private readonly http = inject(HttpClient);
  private readonly accessTokenKey = 'mercure.api.accessToken';
  private readonly refreshTokenKey = 'mercure.api.refreshToken';
  private refreshInFlight$: Observable<ApiTokenResponse> | null = null;

  login(username: string, password: string): Observable<ApiTokenResponse> {
    return this.http.post<ApiTokenResponse>(`${environment.apiBaseUrl}/api/v1/auth/login`, { username, password }).pipe(
      tap(response => {
        sessionStorage.setItem(this.accessTokenKey, response.accessToken);
        if (response.refreshToken) sessionStorage.setItem(this.refreshTokenKey, response.refreshToken);
      }),
    );
  }

  refresh(): Observable<ApiTokenResponse> {
    const refreshToken = sessionStorage.getItem(this.refreshTokenKey);
    if (!refreshToken) return this.http.post<ApiTokenResponse>(`${environment.apiBaseUrl}/api/v1/auth/refresh`, { refreshToken });
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.http.post<ApiTokenResponse>(`${environment.apiBaseUrl}/api/v1/auth/refresh`, { refreshToken }).pipe(
        tap(response => this.storeTokens(response)),
        finalize(() => { this.refreshInFlight$ = null; }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.refreshInFlight$;
  }

  logout(): Observable<void> {
    const refreshToken = sessionStorage.getItem(this.refreshTokenKey);
    return this.http.post<void>(`${environment.apiBaseUrl}/api/v1/auth/logout`, { refreshToken }).pipe(
      tap(() => this.clear()),
    );
  }

  accessToken(): string | null { return sessionStorage.getItem(this.accessTokenKey); }
  refreshToken(): string | null { return sessionStorage.getItem(this.refreshTokenKey); }
  clear(): void { sessionStorage.removeItem(this.accessTokenKey); sessionStorage.removeItem(this.refreshTokenKey); this.refreshInFlight$ = null; }

  private storeTokens(response: ApiTokenResponse): void {
    sessionStorage.setItem(this.accessTokenKey, response.accessToken);
    if (response.refreshToken) sessionStorage.setItem(this.refreshTokenKey, response.refreshToken);
  }
}
